import React, { useState, useRef, useEffect } from "react";
import {
  Box,
  Typography,
  List,
  ListItem,
  ListItemText,
  IconButton,
} from "@mui/material";
import upload from "../../../assets/images/CloudUpload.svg";
import DeleteIcon from "../../../assets/images/deleteIconRed.svg";
import fileText from "../../../assets/images/fileText.svg";

interface FileUploadProps {
  fileTypeLabel?: string;
  fileUrl?: string | null;
  isSingleSelected?: boolean;
  isLink?: boolean;
  onChange: (files: File[]) => void;
}

const FileUpload: React.FC<FileUploadProps> = ({
  fileTypeLabel = "file",
  fileUrl,
  isLink = false,
  isSingleSelected = false,
  onChange,
}) => {
  const [files, setFiles] = useState<File[]>([]);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [isDragging, setIsDragging] = useState(false);

  useEffect(() => {
    if (!isLink && files.length === 0 && fileUrl) {
      setPreviewUrl(fileUrl);
    } else if (isLink || files.length === 0) {
      setPreviewUrl(null);
    }
  }, [fileUrl, isLink, files]);

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFiles = event.target.files
      ? Array.from(event.target.files)
      : [];
    if (selectedFiles.length > 0) {
      setFiles(selectedFiles);
      setPreviewUrl(URL.createObjectURL(selectedFiles[0]));
      onChange(selectedFiles);
    }
  };

  const handleDrop = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    setIsDragging(false);
    if (event.dataTransfer.files.length > 0) {
      const droppedFiles = Array.from(event.dataTransfer.files);
      setFiles(droppedFiles);
      setPreviewUrl(URL.createObjectURL(droppedFiles[0]));
      onChange(droppedFiles);
    }
  };

  const handleDelete = () => {
    setFiles([]);
    setPreviewUrl(null);
    onChange([]);

    if (fileUrl) {
      onChange([]);
    }
  };

  return (
    <Box>
      <Box
        sx={{
          border: "2px dashed #ccc",
          textAlign: "center",
          cursor: "pointer",
          backgroundColor: isDragging ? "#f1f1f1" : "#212121 !important",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          height: "234px",
          transition: "background-color 0.2s",
          position: "relative",
          overflow: "hidden",
        }}
        onClick={() => fileInputRef.current?.click()}
        onDragOver={(e) => {
          e.preventDefault();
          setIsDragging(true);
        }}
        onDragLeave={() => setIsDragging(false)}
        onDrop={handleDrop}
      >
        {!isLink && previewUrl ? (
          fileTypeLabel.toLowerCase() === "image" ? (
            <img
              src={previewUrl}
              alt="Uploaded Preview"
              style={{
                width: "100%",
                height: "100%",
                objectFit: "contain",
              }}
            />
          ) : (
            <>
              <input
                type="file"
                accept={
                  fileTypeLabel.toLowerCase() === "image"
                    ? "image/*"
                    : ".pdf,.xls,.xlsx,.doc,.docx"
                }
                multiple={!isSingleSelected}
                style={{ display: "none" }}
                ref={fileInputRef}
                onChange={handleFileChange}
              />
              <img src={upload} alt="Upload Icon" />
              <Typography
                variant="h6"
                sx={{ mt: 2, fontSize: "16px", fontWeight: "500" }}
              >
                Drop your {fileTypeLabel} here or{" "}
                <Box
                  component="span"
                  sx={{
                    color: "red",
                    fontWeight: "bold",
                    cursor: "pointer",
                    mx: "2px",
                    fontFamily: "Montserrat",
                  }}
                >
                  browse
                </Box>
              </Typography>
              <Typography
                variant="body2"
                sx={{ fontSize: "14px", mt: 1, color: "#E6E6E6" }}
              >
                Max. File Size: 200MB
              </Typography>
            </>
          )
        ) : (
          <>
            <input
              type="file"
              accept={
                fileTypeLabel.toLowerCase() === "image"
                  ? "image/*"
                  : ".pdf,.xls,.xlsx,.doc,.docx"
              }
              multiple={!isSingleSelected}
              style={{ display: "none" }}
              ref={fileInputRef}
              onChange={handleFileChange}
            />
            <img src={upload} alt="Upload Icon" />
            <Typography
              variant="h6"
              sx={{ mt: 2, fontSize: "16px", fontWeight: "500" }}
            >
              Drop your {fileTypeLabel} here or{" "}
              <Box
                component="span"
                sx={{
                  color: "red",
                  fontWeight: "bold",
                  cursor: "pointer",
                  mx: "2px",
                  fontFamily: "Montserrat",
                }}
              >
                browse
              </Box>
            </Typography>
            <Typography
              variant="body2"
              sx={{ fontSize: "14px", mt: 1, color: "#E6E6E6" }}
            >
              Max. File Size: 200MB
            </Typography>
          </>
        )}
      </Box>

      {!isLink && previewUrl && (
        <List sx={{ mt: 1, width: "100%" }}>
          <ListItem sx={{ borderBottom: "1px solid #808080", paddingX: 0 }}>
            <Box
              sx={{
                padding: "8px 10px",
                bgcolor: "#5D5D5D",
                mr: 2,
                display: "flex",
                justifyContent: "center",
                width: "25px",
              }}
            >
              <img src={fileText} alt="File Icon" width={24} height={24} />
            </Box>
            <ListItemText
              sx={{
                "& .MuiTypography-root": {
                  color: "#CCCCCC !important",
                },
              }}
              primary={
                files[0]?.name
                  ? files[0].name.split("_").pop()
                  : fileUrl
                  ? decodeURIComponent(fileUrl.split("/").pop() || "")
                      .split("_")
                      .pop()
                  : "Existing File"
              }
            />
            <IconButton edge="end" onClick={handleDelete}>
              <img
                src={DeleteIcon}
                alt="Delete"
                width={20}
                height={20}
                style={{ marginRight: "10px" }}
              />
            </IconButton>
          </ListItem>
        </List>
      )}
    </Box>
  );
};

export default FileUpload;
